import { useCallback, useEffect, useRef } from "react";

import { type FieldValues, type UseFormReturn } from "react-hook-form";

const DEBOUNCE_DELAY = 1000;

export const useAutoSave = <T extends FieldValues>({
  methods,
  onSave,
  isEnabled,
}: {
  methods: UseFormReturn<T>;
  onSave: () => void;
  isEnabled: boolean;
}) => {
  const { getValues, watch, trigger, formState } = methods;
  const lastSavedValues = useRef<Partial<T>>({});
  const isDirtyRef = useRef(formState.isDirty);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store callbacks and values in refs to use latest values in debounced function
  const onSaveRef = useRef(onSave);
  const isEnabledRef = useRef(isEnabled);
  const triggerRef = useRef(trigger);

  // Keep refs updated
  useEffect(() => {
    onSaveRef.current = onSave;
    isEnabledRef.current = isEnabled;
    triggerRef.current = trigger;
  });

  // Update isDirtyRef whenever formState changes
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    isDirtyRef.current = formState.isDirty;
  }, [formState.isDirty, isEnabled]);

  // Debounced save
  const debouncedSave = useCallback((newValues: T) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(async () => {
      if (!isDirtyRef.current || !isEnabledRef.current) {
        return;
      }

      const isValid = await triggerRef.current();
      if (!isValid) {
        return;
      }

      lastSavedValues.current = newValues;
      void onSaveRef.current();
    }, DEBOUNCE_DELAY);
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const subscription = watch(() => {
      const currentValues = getValues();
      debouncedSave(currentValues);
    });

    lastSavedValues.current = getValues();

    return () => {
      subscription.unsubscribe();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [watch, getValues, isEnabled, debouncedSave]);
};
